import * as React from "react";
import { useSelector } from "react-redux";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../redux/global/actions";
import {
  exportContractsToCSV,
  getContractDetails,
  getContracts,
  getContractsBulk,
} from "../redux/contract/actions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import MultiValueInput from "../inputs/multiValueInput";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect } from "react";
import "./Contracts.css";
import SearchDateInput from "../inputs/searchDateInput";
import MultiSelector from "../inputs/MultiSelector";
import { extractUserInfoFromToken, getLocalStorageSAMLToken } from "../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../widgets/ExportMenu";

const CONTRACT_COLUMNS = [
  {
    id: "contract_reference",
    label: "Contract Reference",
    filter: "text",
    nowrap: true,
  },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "type", label: "Type", filter: "select" },
  { id: "sold_to", label: "Sold To", filter: "text" },
  { id: "status", label: "Status", filter: "select" },
  { id: "orderId", label: "Order ID", filter: "text" },
];

export default function Contracts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryParameters = useSelector((state) => state?.parameters?.contracts);

  const [currentContractReference, setCurrentContractReference] =
    React.useState();
  const [currentContractSoldTo, setCurrentContractSoldTo] = React.useState();
  const [currentContractOrderId, setCurrentContractOrderId] = React.useState();
  const [currentContractBusinessPartner, setCurrentContractBusinessPartner] =
    React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {
      limit: 100,
    };
    console.log("queryParameters", queryParameters);
    if (queryParameters.contractReferences) {
      queryObject.contract_reference = queryParameters.contractReferences;
    }
    if (queryParameters.soldTos) {
      queryObject.sold_to = queryParameters.soldTos;
    }
    if (queryParameters.orderIds) {
      queryObject.order = queryParameters.orderIds;
    }
    if (queryParameters.businessPartners) {
      queryObject.business_partner = queryParameters.businessPartners;
    }
    if (queryParameters.types) {
      queryObject.type = queryParameters.types;
    }
    if (queryParameters.creationDateStart && queryParameters.creationDateEnd) {
      queryObject.creation_date_from = queryParameters.creationDateStart;
      queryObject.creation_date_to = queryParameters.creationDateEnd;
    }
    dispatch(getContracts(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    if (queryParameters.contractReferences) {
      queryObject.contract_reference = queryParameters.contractReferences;
    }
    if (queryParameters.soldTos) {
      queryObject.sold_to = queryParameters.soldTos;
    }
    if (queryParameters.orderIds) {
      queryObject.order = queryParameters.orderIds;
    }
    if (queryParameters.businessPartners) {
      queryObject.business_partner = queryParameters.businessPartners;
    }
    if (queryParameters.types) {
      queryObject.type = queryParameters.types;
    }
    if (queryParameters.creationDateStart && queryParameters.creationDateEnd) {
      queryObject.creation_date_from = queryParameters.creationDateStart;
      queryObject.creation_date_to = queryParameters.creationDateEnd;
    }
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getContractsBulk(queryObject));
  };

  function handleSelectContract(serialNumber, type) {
    dispatch(getContractDetails(serialNumber, type));
    navigate("/contracts/details/" + serialNumber + "/" + type);
  }

  const handleDeleteContractReference = (chipToDelete) => () => {
    dispatch(
      removeParameter({ contracts: { contractReferences: [chipToDelete] } }),
    );
  };
  const handleClearContractReferences = () => {
    dispatch(clearParameters({ contracts: "contractReferences" }));
  };
  const handleDeleteContractSoldTo = (chipToDelete) => () => {
    dispatch(removeParameter({ contracts: { soldTos: [chipToDelete] } }));
  };
  const handleClearContractSoldTos = () => {
    dispatch(clearParameters({ contracts: "soldTos" }));
  };
  const handleDeleteContractOrderId = (chipToDelete) => () => {
    dispatch(removeParameter({ contracts: { orderIds: [chipToDelete] } }));
  };
  const handleClearContractOrderIds = () => {
    dispatch(clearParameters({ contracts: "orderIds" }));
  };
  const handleDeleteContractBusinessPartners = (chipToDelete) => () => {
    dispatch(
      removeParameter({ contracts: { businessPartners: [chipToDelete] } }),
    );
  };
  const handleClearContractBusinessPartners = () => {
    dispatch(clearParameters({ contracts: "businessPartners" }));
  };
  const handleDeleteContractType = (chipToDelete) => () => {
    dispatch(removeParameter({ contracts: { types: [chipToDelete] } }));
  };
  const handleClearContractTypes = () => {
    dispatch(clearParameters({ contracts: "types" }));
  };

  const handleExportToCSV = (contracts) => {
    dispatch(exportContractsToCSV(contracts));
  };
  const typeValues = [
    "Asset Tracking",
    "ClearPass Support Basic",
    "ClearPass Support Software",
    "Complimentary Services",
    "BP Support Plus",
    "BP Long Term Support Plus",
    "BP Support Software",
    "BP Long Term Support Software",
    "BP Extended Support Software",
    "BP Long Term Support",
    "EC Support Plus",
    "EC Long Term Support Plus",
    "EC Support Total",
    "EC Support Software",
    "EC Long Term Support Software",
    "EC Extended Support Software",
    "EC Long Term Support",
    "Dead on arrival",
    "OTEC",
    "FLEX",
    "NaaS",
    "NaaS-OVC-4",
    "NECO EC Support Plus",
    "NECO EC Support Software",
    "NECO BP Support Plus",
    "NECO BP Support Software",
    "OVC Base",
    "OVC Business",
    "OVC Premium",
    "OVNG",
    "OVC10-CAPEX",
    "Purple",
    "PurpleEvergreen",
    "SA-OXO",
    "SPS",
    "SES",
    "SMS",
    "ProServ",
    "Standard Hardware Support RTF",
    "Standard Hardware Support AVR",
    "Support Basic",
  ];

  const contracts = useSelector((state) => {
    if (state.filteredContracts) {
      return state.filteredContracts.map((contract) => {
        return {
          // Because contract_reference isn't always unique, a new identifier is composed from the reference and type
          contract_identifier: contract?.contract_reference + contract.type,
          contract_reference: contract?.contract_reference,
          modification_date: contract?.modification_date,
          creation_date: contract?.creation_date,
          description: contract?.description,
          duration: contract?.duration,
          start_date: contract?.start_date,
          end_date: contract?.end_date,
          part_number: contract?.part_number,
          quantity: contract?.quantity,
          sold_to: contract?.sold_to?.applicant_id,
          status: contract?.status,
          type: contract?.type,
          orderId: contract?.orderId,
        };
      });
    } else return [];
  });

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenBulkMessage(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <MultiValueInput
        setCurrentParam={setCurrentContractReference}
        setQueryParams={setParameters}
        currentParam={currentContractReference}
        queryParams={queryParameters?.contractReferences}
        handleDelete={handleDeleteContractReference}
        handleClear={handleClearContractReferences}
        label="Contract Reference"
        family="contracts"
        type="contractReferences"
      />
      <MultiValueInput
        setCurrentParam={setCurrentContractSoldTo}
        setQueryParams={setParameters}
        currentParam={currentContractSoldTo}
        queryParams={queryParameters?.soldTos}
        handleDelete={handleDeleteContractSoldTo}
        handleClear={handleClearContractSoldTos}
        label="Sold To"
        family="contracts"
        type="soldTos"
      />
      <MultiValueInput
        setCurrentParam={setCurrentContractOrderId}
        setQueryParams={setParameters}
        currentParam={currentContractOrderId}
        queryParams={queryParameters?.orderIds}
        handleDelete={handleDeleteContractOrderId}
        handleClear={handleClearContractOrderIds}
        label="Order ID"
        family="contracts"
        type="orderIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentContractBusinessPartner}
        setQueryParams={setParameters}
        currentParam={currentContractBusinessPartner}
        queryParams={queryParameters?.businessPartners}
        handleDelete={handleDeleteContractBusinessPartners}
        handleClear={handleClearContractBusinessPartners}
        label="Business Partner"
        family="contracts"
        type="businessPartners"
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.types}
        handleDelete={handleDeleteContractType}
        handleClear={handleClearContractTypes}
        label="Type"
        family="contracts"
        type="types"
        options={typeValues}
      />
      <Grid>
        <SearchDateInput
          title={"Creation Date"}
          setFilterDate={setDateParameters}
          family="contracts"
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>
      {contracts.length > 0 && (
        <DataTable
          title="Contracts"
          subtitle={
            contracts.length === 100
              ? `${contracts.length} results, use bulk export for more`
              : `${contracts.length} results`
          }
          columns={CONTRACT_COLUMNS}
          rows={contracts}
          getRowId={(row, i) => `${row?.contract_identifier}-${i}`}
          onRowClick={(row) =>
            handleSelectContract(row?.contract_reference, row?.type)
          }
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              formats={["csv"]}
              onExport={() => handleExportToCSV(rows)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
